import { cn } from '@/shared/helpers/classnames'
import {
  extractResponsiveInitialValue,
  extractResponsiveValue,
} from '@/shared/ui/layouts/responsive-utils'

import { tv } from 'tailwind-variants'

import { GridCellProps, GridColsSpan, GridRowsSpan } from './types'

/**
 * Composant de cellule de grille.
 * À utiliser à l'intérieur d'un Grid pour créer des cellules avec support de colspan et rowspan.
 * Support complet du responsive via les objets de propriétés.
 *
 * @example
 * // Cellule simple occupant 2 colonnes
 * <Grid columns="12">
 *   <Grid.Cell colSpan="2">Contenu</Grid.Cell>
 * </Grid>
 *
 * @example
 * // Cellule avec colspan et rowspan responsives
 * <Grid columns={{ initial: '1', md: '12', lg: '12' }}>
 *   <Grid.Cell colSpan={{ initial: '1', lg: '4' }} rowSpan="2">
 *     Contenu sur plusieurs lignes et colonnes
 *   </Grid.Cell>
 * </Grid>
 *
 * ### Breakpoints disponibles
 * - `initial` : Valeur par défaut (tous les écrans)
 * - `sm` : Small (<= 640px) - applique `sm:` en max-width
 * - `md` : Medium (<= 768px) - applique `md:` en max-width
 * - `lg` : Large (<= 1024px) - applique `lg:` en max-width
 * - `xl` : Extra Large (<= 1280px) - applique `xl:` en max-width
 *
 * **Note:** Les breakpoints utilisent des requêtes max-width (max-sm:, max-md:, max-lg:, max-xl:)
 * ce qui signifie qu'un breakpoint s'applique jusqu'à cette largeur, pas à partir de cette largeur.
 *
 * @param colSpan - Nombre de colonnes à occuper (1-12), support responsive { initial, sm, md, lg, xl }
 * @param rowSpan - Nombre de lignes à occuper (1-6), support responsive { initial, sm, md, lg, xl }
 * @param children - Contenu de la cellule
 */
const GridCellRoot = ({ children, colSpan, rowSpan }: GridCellProps) => {
  // Extraire les valeurs initiales
  const colSpanInitialValue = (extractResponsiveInitialValue(colSpan) || '1') as GridColsSpan
  const rowSpanInitialValue = (extractResponsiveInitialValue(rowSpan) || '1') as GridRowsSpan

  // Extraire les valeurs responsives
  const colSpanSm = extractResponsiveValue('sm', colSpan) as GridColsSpan
  const colSpanMd = extractResponsiveValue('md', colSpan) as GridColsSpan
  const colSpanLg = extractResponsiveValue('lg', colSpan) as GridColsSpan
  const colSpanXl = extractResponsiveValue('xl', colSpan) as GridColsSpan

  const rowSpanSm = extractResponsiveValue('sm', rowSpan) as GridRowsSpan
  const rowSpanMd = extractResponsiveValue('md', rowSpan) as GridRowsSpan
  const rowSpanLg = extractResponsiveValue('lg', rowSpan) as GridRowsSpan
  const rowSpanXl = extractResponsiveValue('xl', rowSpan) as GridRowsSpan

  const cellClasses = cellVariants({
    colSpan: colSpanInitialValue,
    rowSpan: rowSpanInitialValue,
    colSpanSm,
    colSpanMd,
    colSpanLg,
    colSpanXl,
    rowSpanSm,
    rowSpanMd,
    rowSpanLg,
    rowSpanXl,
  })

  return <div className={cn(cellClasses)}>{children}</div>
}

GridCellRoot.displayName = 'GridCell'

export const GridCell = GridCellRoot

/**
 * Variants Tailwind pour la cellule de grille avec support responsive
 */
const cellVariants = tv({
  base: '',
  variants: {
    colSpan: {
      '1': 'col-span-1',
      '2': 'col-span-2',
      '3': 'col-span-3',
      '4': 'col-span-4',
      '5': 'col-span-5',
      '6': 'col-span-6',
      '7': 'col-span-7',
      '8': 'col-span-8',
      '9': 'col-span-9',
      '10': 'col-span-10',
      '11': 'col-span-11',
      '12': 'col-span-12',
    },
    rowSpan: {
      '1': 'row-span-1',
      '2': 'row-span-2',
      '3': 'row-span-3',
      '4': 'row-span-4',
      '5': 'row-span-5',
      '6': 'row-span-6',
    },
    // Variants responsives pour colSpan
    colSpanSm: {
      '1': 'max-sm:col-span-1',
      '2': 'max-sm:col-span-2',
      '3': 'max-sm:col-span-3',
      '4': 'max-sm:col-span-4',
      '5': 'max-sm:col-span-5',
      '6': 'max-sm:col-span-6',
      '7': 'max-sm:col-span-7',
      '8': 'max-sm:col-span-8',
      '9': 'max-sm:col-span-9',
      '10': 'max-sm:col-span-10',
      '11': 'max-sm:col-span-11',
      '12': 'max-sm:col-span-12',
    },
    colSpanMd: {
      '1': 'max-md:col-span-1',
      '2': 'max-md:col-span-2',
      '3': 'max-md:col-span-3',
      '4': 'max-md:col-span-4',
      '5': 'max-md:col-span-5',
      '6': 'max-md:col-span-6',
      '7': 'max-md:col-span-7',
      '8': 'max-md:col-span-8',
      '9': 'max-md:col-span-9',
      '10': 'max-md:col-span-10',
      '11': 'max-md:col-span-11',
      '12': 'max-md:col-span-12',
    },
    colSpanLg: {
      '1': 'max-lg:col-span-1',
      '2': 'max-lg:col-span-2',
      '3': 'max-lg:col-span-3',
      '4': 'max-lg:col-span-4',
      '5': 'max-lg:col-span-5',
      '6': 'max-lg:col-span-6',
      '7': 'max-lg:col-span-7',
      '8': 'max-lg:col-span-8',
      '9': 'max-lg:col-span-9',
      '10': 'max-lg:col-span-10',
      '11': 'max-lg:col-span-11',
      '12': 'max-lg:col-span-12',
    },
    colSpanXl: {
      '1': 'max-xl:col-span-1',
      '2': 'max-xl:col-span-2',
      '3': 'max-xl:col-span-3',
      '4': 'max-xl:col-span-4',
      '5': 'max-xl:col-span-5',
      '6': 'max-xl:col-span-6',
      '7': 'max-xl:col-span-7',
      '8': 'max-xl:col-span-8',
      '9': 'max-xl:col-span-9',
      '10': 'max-xl:col-span-10',
      '11': 'max-xl:col-span-11',
      '12': 'max-xl:col-span-12',
    },
    // Variants responsives pour rowSpan
    rowSpanSm: {
      '1': 'max-sm:row-span-1',
      '2': 'max-sm:row-span-2',
      '3': 'max-sm:row-span-3',
      '4': 'max-sm:row-span-4',
      '5': 'max-sm:row-span-5',
      '6': 'max-sm:row-span-6',
    },
    rowSpanMd: {
      '1': 'max-md:row-span-1',
      '2': 'max-md:row-span-2',
      '3': 'max-md:row-span-3',
      '4': 'max-md:row-span-4',
      '5': 'max-md:row-span-5',
      '6': 'max-md:row-span-6',
    },
    rowSpanLg: {
      '1': 'max-lg:row-span-1',
      '2': 'max-lg:row-span-2',
      '3': 'max-lg:row-span-3',
      '4': 'max-lg:row-span-4',
      '5': 'max-lg:row-span-5',
      '6': 'max-lg:row-span-6',
    },
    rowSpanXl: {
      '1': 'max-xl:row-span-1',
      '2': 'max-xl:row-span-2',
      '3': 'max-xl:row-span-3',
      '4': 'max-xl:row-span-4',
      '5': 'max-xl:row-span-5',
      '6': 'max-xl:row-span-6',
    },
  },
  defaultVariants: {
    colSpan: '1',
    rowSpan: '1',
  },
})
